import React from 'react';
import PropTypes from 'prop-types';

import PubSub from 'pubsub-js';

import api from 'js/api-helper';
import checkApiPropTypes from 'js/check-api-prop-types';
import topics from 'js/topics.json';

import AddToCartResponse from 'components/contact-lens-page/add-to-cart-response';
import Button from 'components/button';
import Form from 'components/form';
import PageSpinner from 'components/page-spinner';
import Select from 'components/select';
import Text from 'components/text';
import Link from 'components/link';
import ValidationErrorTexts from 'components/validation-error-texts';
import ReservInStore from 'components/reserve-in-store/reserve-in-store';
import NotifyMeModal from 'components/notify-me-modal/notify-me-modal';
import cn from 'classnames';

const ActionTypes = {
  addToCard: 'addToCard',
  reserve: 'reserve'
};

const GlassesForm = ({
  addToCartEndpoint,
  reserveEndpoint,
  inputs,
  onUpdate,
  onRef,
  submitLabel,
  registerLabel,
  updateSizeEndpoint,
  validationErrors,
  hideAddToCartButton = false,
  purchaseInformation,
  productCategory,
  productName,
  productBrand,
  notifyMeData
}) => {
  const [isLoading, setLoading] = React.useState(false);
  const [actionType, setActionType] = React.useState(ActionTypes.addToCard);
  const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = React.useState(false);
  const [selectedProductValues, setSelectedProductValues] = React.useState({});

  // Initialize selectedProductValues with single-option inputs and defaultSelectedValue
  React.useEffect(() => {
    const initialValues = {};
    inputs.forEach(input => {
      if (input.options && input.options.length > 0) {
        if (input.options.length === 1) {
          initialValues[input.name] = input.options[0].value;
        } else if (input.defaultSelectedValue) {
          initialValues[input.name] = input.defaultSelectedValue;
        }
      }
    });
    if (Object.keys(initialValues).length > 0) {
      setSelectedProductValues(initialValues);
    }
  }, [inputs]);

  const onSubmit = () => setLoading(true);

  const onResponse = response => {
    if (actionType === ActionTypes.reserve) {
      PubSub.publish(topics.reserveUpdate, response);
      checkApiPropTypes(response, ReservInStore.propTypes, 'ReservInStore');
    } else {
      PubSub.publish(topics.cartUpdate, response);
      checkApiPropTypes(
        response,
        AddToCartResponse.propTypes,
        'AddToCartResponse'
      );
    }
    setLoading(false);
  };

  const onChange = value => {
    if (updateSizeEndpoint) {
      setLoading(true);
      api
        .execute(updateSizeEndpoint, value)
        .then(onUpdate)
        .finally(() => setLoading(false));
    }
  };

  const onSelectOptionsChange = (inputName, value) => {
    setSelectedProductValues(prev => ({
      ...prev,
      [inputName]: value
    }));
  };

  const onNotifyMeButtonClick = () => {
    // Only open the modal if all required options have been selected
    if (Object.keys(selectedProductValues).length !== 0) {
      setIsNotifyMeModalOpen(true);
    }
  };

  if (inputs.length === 0) return null;

  return (
    <>
      <Form
        className="glasses-form"
        endpoint={
          actionType === 'reserve' ? reserveEndpoint : addToCartEndpoint
        }
        onSubmit={onSubmit}
        onChange={onChange}
        onRef={onRef}
        onResponse={onResponse}
        showSubmitButton={false}
        validationErrors={validationErrors}
      >
        {inputs.map((input, i) => {
          return input.options && input.options.length > 0 ? (
            <div className="glasses-form-input-wrapper" key={i}>
              <Text.div
                className={cn('glasses-form-select', {
                  'is-hidden': input.options.length === 1
                })}
                key={input.name}
                theme={Text.themes.small}
              >
                <Select
                  {...input}
                  idPrefix="glasses-select-"
                  labelVisible={true}
                  validations={['isRequired']}
                  labelAnchorScrollText={null}
                  onChange={value => onSelectOptionsChange(input.name, value)}
                />
              </Text.div>
            </div>
          ) : null;
        })}

        <PageSpinner shouldContainInParent={true} isLoading={isLoading} />
        <div className="glasses-form-actions">
          {hideAddToCartButton &&
            purchaseInformation &&
            purchaseInformation.text &&
            purchaseInformation.link && (
              <p className="glasses-form-purchase-information">
                {purchaseInformation.text}
              </p>
            )}
          <div className="glasses-form-button-wrapper">
            {!hideAddToCartButton && (
              <Button
                className="glasses-form-submit"
                data-test-add-to-cart-button
                iconAfter="basket"
                type="submit"
                text={submitLabel}
                themes={[Button.themes.buttonGray]}
                onClick={() => setActionType(ActionTypes.addToCard)}
              />
            )}
            {registerLabel && (
              <Button
                className="glasses-form-register"
                type="submit"
                onClick={() => setActionType(ActionTypes.reserve)}
                text={registerLabel}
                themes={[Button.themes.buttonCyan]}
              />
            )}
            {notifyMeData ? (
              <Button
                className="glasses-form-notify-me-btn"
                iconAfter="mail"
                onClick={onNotifyMeButtonClick}
                text={notifyMeData.buttonLabel}
                themes={[Button.themes.buttonGray]}
              />
            ) : (
              hideAddToCartButton &&
              purchaseInformation &&
              purchaseInformation.link && (
                <Link
                  className="glasses-form-purchase-link"
                  iconAfter="basket"
                  themes={[Link.themes.buttonGray]}
                  {...purchaseInformation.link}
                />
              )
            )}
          </div>
        </div>
      </Form>
      <ReservInStore />
      {notifyMeData && (
        <NotifyMeModal
          {...notifyMeData}
          productName={productName}
          productCategory={productCategory}
          productBrand={productBrand}
          className="glasses-form-notify-me"
          toggleButtonClassName="glasses-form-notify-me-btn"
          isOpen={isNotifyMeModalOpen}
          selectedProductValues={selectedProductValues}
          setIsOpen={setIsNotifyMeModalOpen}
        />
      )}
    </>
  );
};

GlassesForm.propTypes = {
  addToCartEndpoint: PropTypes.string,
  reserveEndpoint: PropTypes.string,
  inputs: PropTypes.arrayOf(PropTypes.exact(Select.propTypes)),
  onUpdate: PropTypes.func,
  onRef: PropTypes.func,
  submitLabel: PropTypes.string,
  registerLabel: PropTypes.string,
  updateSizeEndpoint: PropTypes.string,
  validationErrors: PropTypes.exact(ValidationErrorTexts.propTypes),
  productName: PropTypes.string,
  productCategory: PropTypes.string,
  productBrand: PropTypes.string,
  hideAddToCartButton: PropTypes.bool,
  purchaseInformation: PropTypes.exact({
    text: PropTypes.string,
    link: PropTypes.exact(Link.propTypes)
  }),
  notifyMeData: PropTypes.exact(NotifyMeModal.propTypes)
};

GlassesForm.propTypesMeta = {
  productName: 'exclude',
  productCategory: 'exclude',
  productBrand: 'exclude',
  purchaseInformation: 'exclude'
};

GlassesForm.defaultProps = {
  inputs: [],
  onRef: () => {}
};

export default GlassesForm;
